import { useEffect, useMemo, useState } from "react";
import {
  ResponsiveContainer,
  LineChart,
  Line,
  XAxis,
  YAxis,
  Legend,
  CartesianGrid,
  Tooltip,
} from "recharts";
import "./SummaryCharts.css";
// @ts-ignore
import { api } from "../../../api/client.tsx";
// @ts-ignore
import TopSummaryCharts from "../TopSummaryCharts/TopSummaryCharts.tsx";
// @ts-ignore
import { Loader } from "../../../Loading/Loading.tsx";

const LINE_SERIES = ["Low", "Medium", "High", "Critical", "Unknown", "Total"] as const;
type LineSeries = (typeof LINE_SERIES)[number];

type L1Row = {
  image?: string;
  Image?: string;
  version?: string;
  critical_persisting?: number;
  high_persisting?: number;
  medium_persisting?: number;
  low_persisting?: number;
  unknown_persisting?: number;
  critical_obsolete?: number;
  high_obsolete?: number;
  medium_obsolete?: number;
  low_obsolete?: number;
  unknown_obsolete?: number;
  creation_time?: string | null;
};

const LINE_COLORS: Record<LineSeries, string> = {
  Low: "#2ecc71",
  Medium: "#f1c40f",
  High: "#f39c12",
  Critical: "#e74c3c",
  Unknown: "#9CA3AF",
  Total: "#3b82f6",
};

const FLOW_COLORS = {
  Persisted: "#5B7CFF",
  Added: "#10b981",
  Deleted: "#9CA3AF",
  Total: "#334155",
} as const;

function repoPrefixFromImageId(id: string): string {
  const s = String(id ?? "");
  const m = s.match(/^([^/_-]+)/);
  return m ? m[1] : "";
}

function toTs(s?: string | null): number | undefined {
  if (!s) return undefined;
  const clean = s.replace(" ", "T");
  const t = Date.parse(clean);
  return Number.isFinite(t) ? t : undefined;
}

const dateShort = new Intl.DateTimeFormat(undefined, {
  year: "numeric",
  month: "short",
  day: "2-digit",
});
const dateLong = new Intl.DateTimeFormat(undefined, {
  year: "numeric",
  month: "short",
  day: "2-digit",
  hour: "2-digit",
  minute: "2-digit",
});

function fmtTickShort(ts: number) {
  try {
    return dateShort.format(ts);
  } catch {
    return String(ts);
  }
}

function fmtTickLong(ts: number) {
  try {
    return dateLong.format(ts);
  } catch {
    return String(ts);
  }
}

function SevTooltip({ active, label, payload }: any) {
  if (!active || !payload || payload.length === 0) return null;

  const byName: Record<string, number> = {};
  payload.forEach((p: any) => {
    byName[p.name] = p.value;
  });

  // 👇 NEW: get version string for this point
  const version: string | undefined = payload[0]?.payload?.version;

  return (
    <div className="sev-tt">
      <div className="sev-tt-title">
        Published: {fmtTickLong(Number(label))}
        {version && (
          <>
            {" "}
            — Version: <span className="sev-tt-version">{version}</span>
          </>
        )}
      </div>
      <div className="sev-tt-row">
        {(["Low", "Medium", "High", "Critical", "Unknown", "Total"] as LineSeries[]).map(
          (k) =>
            k in byName ? (
              <div key={k} className="sev-tt-item">
                <span className="dot" style={{ background: LINE_COLORS[k] }} />
                <span className="name">{k}:</span>
                <span className="val">{Number(byName[k]).toLocaleString()}</span>
              </div>
            ) : null
        )}
      </div>
    </div>
  );
}

export default function SummaryCharts({ selectedImage }: { selectedImage: string }) {
  const [l1Rows, setL1Rows] = useState<L1Row[]>([]);
  const [loading, setLoading] = useState<boolean>(false);

  const [dataset] = useState<"flow" | "severity">("severity");
  const [visibleFlow, setVisibleFlow] = useState<
    Record<keyof typeof FLOW_COLORS, boolean>
  >({
    Persisted: true,
    Added: false,
    Deleted: true,
    Total: true,
  });
  const [visibleSev, setVisibleSev] = useState<Record<LineSeries, boolean>>({
    Low: true,
    Medium: true,
    High: true,
    Critical: true,
    Unknown: true,
    Total: true,
  });

  const repoPrefix = useMemo(
    () => repoPrefixFromImageId(selectedImage),
    [selectedImage]
  );

  useEffect(() => {
    if (!repoPrefix) return;

    let cancelled = false;

    (async () => {
      setLoading(true);
      try {
        const bundle = await api.stats.level1Bundle(repoPrefix);
        if (cancelled) return;

        const rows = Array.isArray(bundle?.versions) ? (bundle.versions as L1Row[]) : [];
        setL1Rows(rows);
      } catch (err) {
        if (!cancelled) {
          console.error("SummaryCharts level1Bundle error:", err);
          setL1Rows([]);
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [repoPrefix]);

  const labelForRow = (r: L1Row) => {
    const image = (r.image ?? r.Image ?? "") as string;
    if (image.startsWith(repoPrefix + "_")) return image.slice(repoPrefix.length + 1);
    if (r.version) return String(r.version);
    return image.split("_").slice(1).join("_") || image || "v";
  };

  const rowsWithTime = useMemo(() => {
    const mapped = l1Rows.map((r, i) => ({ row: r, ts: toTs(r.creation_time), i }));
    const STEP = 60_000;
    let lastKnown = Date.now();
    const minValid = Math.min(...mapped.map((m) => m.ts ?? Infinity));
    if (Number.isFinite(minValid)) lastKnown = minValid;
    for (const m of mapped) {
      if (m.ts === undefined) {
        lastKnown += STEP;
        m.ts = lastKnown;
      }
    }
    mapped.sort((a, b) => (a.ts! - b.ts!) || (a.i - b.i));
    return mapped.map((m) => ({ r: m.row, ts: m.ts as number }));
  }, [l1Rows]);

  const lineDataFlow = useMemo(() => {
    return rowsWithTime.map(({ r, ts }) => {
      const p =
        (r.critical_persisting ?? 0) +
        (r.high_persisting ?? 0) +
        (r.medium_persisting ?? 0) +
        (r.low_persisting ?? 0) +
        (r.unknown_persisting ?? 0);
      const d =
        (r.critical_obsolete ?? 0) +
        (r.high_obsolete ?? 0) +
        (r.medium_obsolete ?? 0) +
        (r.low_obsolete ?? 0) +
        (r.unknown_obsolete ?? 0);
      const a = 0;
      const t = p + a + d;
      return { ts, version: labelForRow(r), Persisted: p, Added: a, Deleted: d, Total: t };
    });
  }, [rowsWithTime, repoPrefix]);

  const lineDataSeverity = useMemo(() => {
    return rowsWithTime.map(({ r, ts }) => {
      const low = (r.low_persisting ?? 0) + (r.low_obsolete ?? 0);
      const med = (r.medium_persisting ?? 0) + (r.medium_obsolete ?? 0);
      const high = (r.high_persisting ?? 0) + (r.high_obsolete ?? 0);
      const crit = (r.critical_persisting ?? 0) + (r.critical_obsolete ?? 0);
      const unk = (r.unknown_persisting ?? 0) + (r.unknown_obsolete ?? 0);
      const total = low + med + high + crit + unk;
      return {
        ts,
        version: labelForRow(r),
        Low: low,
        Medium: med,
        High: high,
        Critical: crit,
        Unknown: unk,
        Total: total,
      };
    });
  }, [rowsWithTime, repoPrefix]);

  const data = dataset === "flow" ? lineDataFlow : lineDataSeverity;

  const xTicks = useMemo(() => {
    if (!data.length) return undefined;
    const ts = data.map((d) => d.ts).sort((a, b) => a - b);
    const min = ts[0];
    const max = ts[ts.length - 1];
    const MAX = 6;
    if (ts.length <= MAX) return ts;
    const step = (max - min) / (MAX - 1);
    const ticks: number[] = [];
    for (let i = 0; i < MAX; i++) ticks.push(Math.round(min + i * step));
    return ticks;
  }, [data]);

  return (
    <div className="charts-grid">
      <section className="chart-top">
        <TopSummaryCharts selectedImage={selectedImage} />
      </section>

      <section className="chart-line">
        <div className="chart-box-line">
          <h4 className="chart-title">Version Trends (by publish date)</h4>

          <div className="chart-toolbar">
            <div className="segmented">
              <button
                className={`seg ${dataset === "severity" ? "is-active" : ""}`}
                title="Low / Medium / High / Critical / Unknown / Total"
              >
                Severity: L / M / H / C / U / Total
              </button>
            </div>

            {dataset === "flow" ? (
              <div className="pillset">
                {(["Persisted", "Added", "Deleted", "Total"] as const).map((k) => (
                  <button
                    key={k}
                    className={`pill ${visibleFlow[k] ? "on" : ""}`}
                    style={{ borderColor: FLOW_COLORS[k], color: FLOW_COLORS[k] }}
                    onClick={() =>
                      setVisibleFlow((v) => ({
                        ...v,
                        [k]: !v[k],
                      }))
                    }
                  >
                    {k}
                  </button>
                ))}
              </div>
            ) : (
              <div className="pillset">
                {(["Low", "Medium", "High", "Critical", "Unknown", "Total"] as const).map(
                  (k) => (
                    <button
                      key={k}
                      className={`pill ${visibleSev[k] ? "on" : ""}`}
                      style={{ borderColor: LINE_COLORS[k], color: LINE_COLORS[k] }}
                      onClick={() =>
                        setVisibleSev((v) => ({
                          ...v,
                          [k]: !v[k],
                        }))
                      }
                    >
                      {k}
                    </button>
                  )
                )}
              </div>
            )}
          </div>

          {loading ? (
            <div className="empty">
              <Loader />
            </div>
          ) : !l1Rows.length ? (
            <div className="empty">No data for this repository.</div>
          ) : (
            <div className="chart-inner">
              <ResponsiveContainer width="100%" height="100%">
                <LineChart
                  data={data}
                  margin={{ top: 24, right: 24, left: 8, bottom: 32 }}
                >
                  <CartesianGrid strokeDasharray="3 3" />
                  <XAxis
                    dataKey="ts"
                    type="number"
                    scale="time"
                    domain={["auto", "auto"]}
                    ticks={xTicks}
                    tickFormatter={fmtTickShort}
                    tick={{ fontSize: 12, fill: "#111827" }}
                    tickMargin={12}
                    minTickGap={20}
                    interval="preserveStartEnd"
                  />
                  <YAxis
                    tickLine={false}
                    axisLine={false}
                    label={{
                      value: "CVE's",
                      angle: -90,
                      position: "insideLeft",
                      dx: 10,
                      style: {
                        fontSize: 12,
                        fontWeight: 500,
                      },
                    }}
                    allowDecimals={false}
                    tick={{ fontSize: 13, fill: "#111827" }}
                  />
                  {dataset === "severity" ? (
                    <Tooltip
                      content={<SevTooltip />}
                      labelFormatter={(v) => fmtTickLong(Number(v))}
                    />
                  ) : (
                    <Tooltip labelFormatter={(v) => fmtTickLong(Number(v))} />
                  )}
                  <Legend wrapperStyle={{ fontSize: 14 }} />

                  {dataset === "severity" && visibleSev.High && (
                    <Line
                      type="monotone"
                      dataKey="High"
                      stroke={LINE_COLORS.High}
                      strokeWidth={3}
                      dot={{ r: 2 }}
                      activeDot={{ r: 5 }}
                    />
                  )}
                  {dataset === "severity" && visibleSev.Critical && (
                    <Line
                      type="monotone"
                      dataKey="Critical"
                      stroke={LINE_COLORS.Critical}
                      strokeWidth={3}
                      dot={{ r: 2 }}
                      activeDot={{ r: 5 }}
                    />
                  )}
                  {dataset === "severity" && visibleSev.Unknown && (
                    <Line
                      type="monotone"
                      dataKey="Unknown"
                      stroke={LINE_COLORS.Unknown}
                      strokeWidth={3}
                      dot={{ r: 2 }}
                      activeDot={{ r: 5 }}
                    />
                  )}
                  {dataset === "severity" && visibleSev.Total && (
                    <Line
                      type="monotone"
                      dataKey="Total"
                      stroke={LINE_COLORS.Total}
                      strokeWidth={3}
                      dot={{ r: 2 }}
                      activeDot={{ r: 5 }}
                    />
                  )}
                </LineChart>
              </ResponsiveContainer>
            </div>
          )}
        </div>
      </section>
    </div>
  );
}
